import { useMutation, type ApolloError } from '@apollo/client'
import { useCallback } from 'react'
import { useParams } from 'react-router-dom'
import {
  CART_REMOVE_PRODUCT,
  type ICartRemoveProduct,
  type IRemoveProductParams,
} from '@commerce/core'

export interface UseRemoveFromCart {
  removeItem: (sku: string) => void
  error: ApolloError | undefined
  loading: boolean
}

/**
 * Removes a line from the cart.
 *
 * Same shape as `useAddToCart`: the mutation returns the whole cart with its
 * `id`, so Apollo updates the normalised entity and the page re-renders without
 * a refetch, and `onError` keeps a failed removal from becoming an unhandled
 * rejection.
 */
export const useRemoveFromCart = (): UseRemoveFromCart => {
  const { slug = '', sessionId = '' } = useParams()
  const [mutate, { error, loading }] = useMutation<ICartRemoveProduct, IRemoveProductParams>(
    CART_REMOVE_PRODUCT,
    { onError: () => undefined },
  )

  const removeItem = useCallback(
    (sku: string) => {
      void mutate({
        variables: {
          cartRemoveProductSessionUid: sessionId,
          cartRemoveProductStorefrontName: slug,
          cartRemoveProductData: { sku },
        },
      })
    },
    [mutate, slug, sessionId],
  )

  return { removeItem, error, loading }
}
